import { defineStore } from 'pinia';
import { authApi, type AdminProfile, type MenuNode } from '@/api/system';
import { WORKSPACES, menuWorkspace, type WorkspaceKey } from '@/config/workspaces';

const TOKEN_KEY = 'lulupaw_admin_token';
const WORKSPACE_KEY = 'lulupaw_admin_workspace';

interface AuthState {
  token: string;
  profile: AdminProfile | null;
  /** 当前进入的工作区（app / mall）；未选时为空，路由守卫会导向选择页。 */
  workspace: WorkspaceKey | '';
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    token: localStorage.getItem(TOKEN_KEY) ?? '',
    profile: null,
    workspace: (localStorage.getItem(WORKSPACE_KEY) as WorkspaceKey | null) ?? ''
  }),

  getters: {
    isAuthenticated: (s): boolean => !!s.token,
    /** 该账号被授予的全部菜单（未按工作区过滤）。 */
    allMenus: (s): MenuNode[] => s.profile?.menus ?? [],
    /** 当前工作区下的菜单（侧边栏用）。 */
    menus(s): MenuNode[] {
      const ws = s.workspace;
      if (!ws) return [];
      return (s.profile?.menus ?? []).filter((m) => menuWorkspace(m) === ws);
    },
    /** 该账号有权访问的工作区（菜单里出现过的工作区，按 WORKSPACES 顺序）。 */
    availableWorkspaces(s): WorkspaceKey[] {
      const present = new Set((s.profile?.menus ?? []).map(menuWorkspace));
      return WORKSPACES.map((w) => w.key).filter((k) => present.has(k));
    },
    permissions: (s): string[] => s.profile?.permissions ?? [],
    isSuperAdmin: (s): boolean => s.profile?.superAdmin ?? false
  },

  actions: {
    setToken(token: string) {
      this.token = token;
      if (token) {
        localStorage.setItem(TOKEN_KEY, token);
      } else {
        localStorage.removeItem(TOKEN_KEY);
      }
    },

    /** 选择/切换工作区（持久化，刷新后保留）。 */
    setWorkspace(ws: WorkspaceKey | '') {
      this.workspace = ws;
      if (ws) {
        localStorage.setItem(WORKSPACE_KEY, ws);
      } else {
        localStorage.removeItem(WORKSPACE_KEY);
      }
    },

    async login(username: string, password: string): Promise<void> {
      const res = await authApi.login(username, password);
      this.setToken(res.accessToken);
      this.profile = res.profile;
    },

    /** 刷新页面后用已存 token 拉取当前账号资料；失败抛错由调用方处理。 */
    async fetchProfile(): Promise<void> {
      this.profile = await authApi.me();
    },

    /** 确保 profile 已加载（路由守卫调用）。 */
    async ensureProfile(): Promise<void> {
      if (!this.profile && this.token) {
        await this.fetchProfile();
      }
    },

    hasPermission(code: string): boolean {
      if (!code) return true;
      if (this.isSuperAdmin) return true;
      return this.permissions.includes(code);
    },

    async logout(): Promise<void> {
      try {
        if (this.token) {
          await authApi.logout();
        }
      } catch {
        // 无状态登出，忽略网络错误
      }
      this.clearSession();
    },

    /** 清空本地登录态（不发网络登出）。用于 401：token 已失效，直接就地清理。 */
    clearSession() {
      this.setToken('');
      this.setWorkspace('');
      this.profile = null;
    }
  }
});
